<template>
  <div class="outreach-list" style="padding: 1.5rem;">

    <!-- No templates warning -->
    <div v-if="!hasTemplates && !configLoading" class="notice notice--warning" style="margin-bottom: 1.5rem;">
      <strong>No email templates configured.</strong>
      You need to set up at least one SendGrid email template before you can send outreach invitations.
      <ds-button size="small" variant="secondary" label="Configure templates →" @click="$emit('switch-tab', 'settings')" />
    </div>

    <!-- Import section -->
    <div class="outreach-list__import" style="margin-bottom: 1.5rem; padding: 1rem; background: #f8f8f8; border-radius: 4px;">
      <h4 style="margin-bottom: 0.5rem;">Import prospects</h4>
      <p style="margin-bottom: 0.75rem; font-size: 0.875rem; color: #666;">
        Upload a CSV file (max 500 rows) with columns: email, name, country_code, website_url. Additional source columns are optional and used for richer matching when available.
      </p>
      <div class="tw-flex items-center gap-2">
        <input
          ref="fileInput"
          type="file"
          accept=".csv"
          class="hidden"
          @change="onFileSelected"
        />
        <ds-button
          variant="secondary"
          :label="selectedFileName || 'Choose CSV file'"
          @click="$refs.fileInput.click()"
        />
        <ds-button
          variant="primary"
          :label="importing ? 'Importing…' : 'Import prospects'"
          :disabled="!selectedFileName || importing"
          @click="importFile"
        />
      </div>
      <div v-if="importResult" class="notice notice--success" style="margin-top: 0.75rem;">
        Imported <strong>{{ importResult.imported }}</strong> prospects.
        <span v-if="importResult.skipped > 0">
          Skipped <strong>{{ importResult.skipped }}</strong>
          ({{ importResult.skipped_reasons.duplicate }} duplicate,
          {{ importResult.skipped_reasons.missing_email }} missing email<template v-if="importResult.skipped_reasons.already_claimed">,
          {{ importResult.skipped_reasons.already_claimed }} already claimed</template>).
        </span>
      </div>
      <div v-if="importError" class="notice notice--error" style="margin-top: 0.75rem;">
        {{ importError }}
      </div>
    </div>

    <!-- Country filter -->
    <div class="tw-flex flex-row flex-center" style="margin-bottom: 1rem; align-items: center;" v-if="displayFilter">
      <label style="font-weight: 500;">Filter by country:</label>
      <CountryDropdown
        class="max-w-[150px] ml-4"
        style="width: 200px !important;"
        @update:modelValue="onCountryFilterInput"
      />
      <span v-if="countryFilter" style="color: #888; font-size: 0.875rem;">
        Showing {{ total }} result{{ total !== 1 ? 's' : '' }}
      </span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="tw-flex items-center justify-center" style="padding: 2rem;">
      <LoadingNew/>
    </div>

    <template v-else>
      <!-- Empty state -->
      <div v-if="prospects.length === 0" style="padding: 2rem; text-align: center; color: #888;">
        No prospects found{{ countryFilter ? ' for the selected country filter' : '' }}.
        Import a CSV file to get started.
      </div>

      <template v-else>
        <!-- Send toolbar -->
        <div class="tw-flex items-center gap-2" style="margin-bottom: 0.75rem; padding: 0.75rem; background: #f0f4ff; border-radius: 4px;">
          <span style="font-size: 0.875rem;">
            {{ selectedCount }} selected
          </span>
          <dropdown
            placeholder="Select template…"
            width="220px"
            :model-value="chosenTemplateId"
            :options="templateOptions"
            :disabled="!hasTemplates"
            @update:modelValue="chosenTemplateId = $event"
          />
          <ds-button
            variant="primary"
            :label="sending ? 'Sending…' : 'Send invitations'"
            :disabled="selectedCount === 0 || !chosenTemplateId || sending"
            @click="sendInvitations"
          />
          <span v-if="sendResult" style="color: green; font-size: 0.875rem;">
            Sent out {{ sendResult.sent }} email(s) {{ sendResult.failed > 0 ? `, ${sendResult.failed} emails failed` : '' }}.
          </span>
          <span v-if="sendError" style="color: red; font-size: 0.875rem;">
            {{ sendError }}
          </span>
        </div>

        <!-- Prospects table -->
        <table class="table">
          <thead>
            <tr>
              <th style="width: 40px;">
                <input
                  type="checkbox"
                  :checked="allPageSelected"
                  :indeterminate.prop="somePageSelected && !allPageSelected"
                  @change="toggleSelectAll"
                />
              </th>
              <th>Company</th>
              <th>Email</th>
              <th>Country</th>
              <th>Website</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prospect in prospects" :key="prospect.id">
              <td>
                <input
                  type="checkbox"
                  :checked="!!selected[prospect.id]"
                  @change="toggleSelect(prospect.id)"
                />
              </td>
              <td>{{ prospect.company_name || '—' }}</td>
              <td>{{ prospect.email }}</td>
              <td>{{ prospect.country_code || '—' }}</td>
              <td>
                <a
                  v-if="prospect.website_url"
                  :href="prospect.website_url"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ prospect.website_url }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>

        <pagination
          class="simple-pagination--bottom"
          :paging="paging"
          @change="setOffset"
        />
      </template>
    </template>
  </div>
</template>

<script lang="ts">
  import { fetchOutreachConfig, fetchProspects, importProspects, sendOutreach } from '../../../api/outreach'
  import { defineComponent } from 'vue'
  import _throttle from 'lodash/throttle'
  import Pagination from '../../Pagination/SimplePagination.vue'
  import LoadingNew from '@/components/Loaders/LoadingNew.vue'
  import Dropdown from '../../Dropdown/Dropdown.vue'
  import CountryDropdown from '@/components/Dropdown/CountryDropdown.vue'

  export default defineComponent({
    name: 'outreach-list',
    components: { CountryDropdown, LoadingNew, Pagination, Dropdown },
    emits: ['switch-tab'],
    data () {
      return {
        prospects: [],
        paging: {},
        total: 0,
        loading: false,
        offset: 0,
        countryFilter: '',
        selected: {},
        outreachTemplates: [],
        chosenTemplateId: '',
        hasTemplates: false,
        configLoading: false,
        selectedFileName: '',
        importing: false,
        importResult: null,
        importError: null,
        sending: false,
        sendResult: null,
        sendError: null,
      }
    },
    computed: {
      templateOptions () {
        return this.outreachTemplates.map(tpl => ({
          label: tpl.name,
          value: tpl.template_id,
        }))
      },
      displayFilter (): boolean {
        return this.prospects.some(p => !!p.country_code) || this.countryFilter
      },
      selectedCount (): number {
        return Object.keys(this.selected).length
      },
      allPageSelected (): boolean {
        return this.prospects.length > 0 && this.prospects.every(p => !!this.selected[p.id])
      },
      somePageSelected (): boolean {
        return this.prospects.some(p => !!this.selected[p.id])
      },
    },
    mounted () {
      this.fetchConfig()
      this.fetchList()
    },
    methods: {
      fetchConfig () {
        this.configLoading = true
        fetchOutreachConfig()
          .then((data) => {
            this.outreachTemplates = data.email_templates || []
            this.hasTemplates = this.outreachTemplates.length > 0
          })
          .catch(() => {})
          .finally(() => {
            this.configLoading = false
          })
      },
      fetchList () {
        this.loading = true
        const filters = { limit: 50, offset: this.offset }
        if (this.countryFilter) {
          filters.country_code = this.countryFilter
        }
        fetchProspects(filters)
          .then((response) => {
            this.prospects = response.data?.data ?? []
            this.paging = response.paging ?? {}
            this.total = parseInt(response.total ?? 0, 10)
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false
          })
      },
      setOffset (offset) {
        this.offset = offset
        this.selected = {}
        this.fetchList()
      },
      onCountryFilterInput: _throttle(function (selectedOption) {
        this.countryFilter = selectedOption?.code || ''
        this.offset = 0
        this.selected = {}
        this.fetchList()
      }, 300, { trailing: true }),
      toggleSelect (id) {
        const next = { ...this.selected }
        if (next[id]) {
          delete next[id]
        } else {
          next[id] = true
        }
        this.selected = next
      },
      toggleSelectAll () {
        if (this.allPageSelected) {
          const next = { ...this.selected }
          this.prospects.forEach(p => delete next[p.id])
          this.selected = next
        } else {
          const next = { ...this.selected }
          this.prospects.forEach(p => { next[p.id] = true })
          this.selected = next
        }
      },
      onFileSelected () {
        this.selectedFileName = this.$refs.fileInput?.files[0]?.name ?? ''
        this.importResult = null
        this.importError = null
      },
      importFile () {
        const file = this.$refs.fileInput?.files[0]
        if (!this.selectedFileName || !file) {
          return
        }

        this.importing = true
        this.importResult = null
        this.importError = null

        const formData = new FormData()
        formData.append('file', file)

        importProspects(formData)
          .then((data) => {
            this.importResult = data
            this.selectedFileName = ''
            this.$refs.fileInput.value = ''
            this.offset = 0
            this.fetchList()
          })
          .catch((err) => {
            this.importError = err?.message || 'Import failed. Please check the file format and try again.'
          })
          .finally(() => {
            this.importing = false
          })
      },
      sendInvitations () {
        if (this.selectedCount === 0 || !this.chosenTemplateId) return
        this.sending = true
        this.sendResult = null
        this.sendError = null
        sendOutreach({
          prospect_ids: Object.keys(this.selected).map(Number),
          template_id: this.chosenTemplateId,
        })
          .then((data) => {
            this.sendResult = data
            this.selected = {}
            this.chosenTemplateId = ''
            this.offset = 0
            this.fetchList()
          })
          .catch((err) => {
            this.sendError = err?.message || 'Failed to send invitations. Please try again.'
          })
          .finally(() => {
            this.sending = false
          })
      },
    },
  })
</script>
